import { resolveId } from '@medplum/core'
import {
  type DateString,
  endOfDateInLocalTimezone,
  parseDateString,
  startOfDateInLocalTimezone,
  toDateString,
  today,
} from '@seen/core/dates'
import { invariant } from '@seen/core/invariant'
import type * as appointments from '@seen/core/models/appointments/index'
import { Participant } from '@seen/core/models/patients/participant'
import { ExtendedQuestionnaireResponse } from '@seen/core/models/questionnaireResponse'
import * as questionnaires from '@seen/core/models/questionnaires/index'
import {
  Button,
  MiniCalendar,
  MiniCalendarDay,
  MiniCalendarDays,
  MiniCalendarNavigation,
} from '@seen/ui'
import { IconPrinter } from '@tabler/icons-react'
import { groupBy, sortBy, uniqBy } from 'lodash-es'
import pluralize from 'pluralize'
import { type JSX, useCallback, useMemo, useState } from 'react'
import { AppointmentDetails } from '#src/components/appointment/AppointmentDetails.tsx'
import { AppointmentDisplay } from '#src/components/appointment/AppointmentDisplay.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import type { ColumnConfig } from '#src/components/fhir-table/FhirMRTTable.constants.tsx'
import { ReactTable } from '#src/components/fhir-table/ReactTable.tsx'
import {
  FilterChip,
  type FilterChipOption,
} from '#src/components/filter-chip/FilterChip.tsx'
import { Sheet, SheetContent } from '#src/components/index.ts'
import { ParticipantFlags } from '#src/components/ParticipantFlags.tsx'
import { AddToMealQueue } from '#src/components/patient/ops/AddToMealQueue.tsx'
import { CheckInButton } from '#src/components/patient/ops/CheckInButton.tsx'
import { CheckOutButton } from '#src/components/patient/ops/CheckOutButton.tsx'
import { PatientCard } from '#src/components/patient/PatientCard.tsx'
import { QuestionnaireItemForm } from '#src/components/questionnaire-form/questionnaire-item-form/QuestionnaireItemForm.tsx'
import { ParticipantReferenceContext } from '#src/contexts/current-participant/ParticipantReferenceContext.tsx'
import { useAppointments } from '#src/hooks/useAppointments.ts'
import { useNavigateFromClickEvent } from '#src/hooks/useNavigateFromClickEvent.ts'
import {
  type ParallelSearchOneQuery,
  useParallelSearchOne,
} from '#src/hooks/useParallelSearchOne.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

interface DailyAttendanceItemData {
  participant: Participant
  questionnaireResponse: ExtendedQuestionnaireResponse | null
  dietQuestionnaireResponse: ExtendedQuestionnaireResponse | null
  appointments: appointments.ExtendedAppointment[]
  rowNumber: number
}

type LocationFilterValue = 'onsite' | 'offsite' | 'checkedIn' | 'notCheckedIn'

const locationOptions: FilterChipOption<LocationFilterValue>[] = [
  { value: 'onsite', label: 'No offsite appointments', keywords: ['onsite'] },
  { value: 'offsite', label: 'Offsite appointments', keywords: ['offsite'] },
  { value: 'checkedIn', label: 'Checked in', keywords: ['checked-in'] },
  {
    value: 'notCheckedIn',
    label: 'Not checked in',
    keywords: ['not-checked-in'],
  },
]

export function DailyAttendance(): JSX.Element {
  const [selectedDate, setSelectedDate] = useState<DateString>(today())
  const [selectedLocationFilters, setSelectedLocationFilters] = useState<
    LocationFilterValue[]
  >([])
  const [selectedAppointment, setSelectedAppointment] = useState<
    appointments.ExtendedAppointment | undefined
  >()

  const handleDateChange = useCallback((date: Date | undefined) => {
    setSelectedDate(date ? toDateString(date) : today())
  }, [])

  const selectedDateAsDate = useMemo(
    () => parseDateString(selectedDate),
    [selectedDate]
  )

  const { start, end } = useMemo(
    () => ({
      start: startOfDateInLocalTimezone(selectedDate),
      end: endOfDateInLocalTimezone(selectedDate),
    }),
    [selectedDate]
  )

  const appointmentsResult = useAppointments({
    start,
    end,
    statuses: ['booked'],
  })

  const appointmentsByParticipantId = useMemo(
    () =>
      groupBy(
        appointmentsResult.data?.filter((a) => !!a.patient) ?? [],
        (appointment) => resolveId(appointment.patient)
      ),
    [appointmentsResult.data]
  )

  const participantIds = useMemo(
    () => Object.keys(appointmentsByParticipantId),
    [appointmentsByParticipantId]
  )

  const participants = useSearch(
    Participant,
    {
      _id: participantIds,
    },
    {
      query: {
        enabled: participantIds.length > 0,
      },
    }
  )

  const questionnaireQueries = useMemo(
    () =>
      participantIds.map((participantId) => ({
        resourceClass: ExtendedQuestionnaireResponse,
        query: ExtendedQuestionnaireResponse.searchQuery({
          questionnaire: questionnaires.AdditionalInformationQuestionnaire,
          subject: { reference: `Patient/${participantId}` },
        }),
      })) as ParallelSearchOneQuery<ExtendedQuestionnaireResponse>[],
    [participantIds]
  )

  const questionnaireResults = useParallelSearchOne(questionnaireQueries, {
    query: {
      enabled: questionnaireQueries.length > 0,
    },
  })

  const dietQuestionnaireQueries = useMemo(
    () =>
      participantIds.map((participantId) => ({
        resourceClass: ExtendedQuestionnaireResponse,
        query: ExtendedQuestionnaireResponse.searchQuery({
          questionnaire: questionnaires.DietQuestionnaire,
          subject: { reference: `Patient/${participantId}` },
        }),
      })) as ParallelSearchOneQuery<ExtendedQuestionnaireResponse>[],
    [participantIds]
  )

  const dietQuestionnaireResults = useParallelSearchOne(
    dietQuestionnaireQueries,
    {
      query: {
        enabled: dietQuestionnaireQueries.length > 0,
      },
    }
  )

  const tableData = useMemo<DailyAttendanceItemData[]>(() => {
    const qrsByParticipantRef = new Map(
      questionnaireResults.map((result) => [
        result.data?.subjectPatient?.reference,
        result.data,
      ])
    )

    const dietQrsByParticipantRef = new Map(
      dietQuestionnaireResults.map((result) => [
        result.data?.subjectPatient?.reference,
        result.data,
      ])
    )

    const attendanceData = (participants.data ?? []).map(
      (participant, index) => {
        const questionnaireResponse =
          qrsByParticipantRef.get(participant.reference.reference) ?? null
        const dietQuestionnaireResponse =
          dietQrsByParticipantRef.get(participant.reference.reference) ?? null
        return {
          participant,
          questionnaireResponse,
          dietQuestionnaireResponse,
          appointments:
            sortBy(
              appointmentsByParticipantId[participant.id],
              (a) => a.start
            ) ?? [],
          rowNumber: index + 1,
        }
      }
    )

    return attendanceData.filter((data) => {
      // No filters = show all
      if (selectedLocationFilters.length === 0) {
        return true
      }

      // Check each selected filter (OR logic)
      return selectedLocationFilters.some((filter) => {
        switch (filter) {
          case 'onsite':
            return !data.appointments.some((a) => a.isSpecialistVisit)
          case 'offsite':
            return data.appointments.some((a) => a.isSpecialistVisit)
          case 'checkedIn':
            return data.participant.isOnsite
          case 'notCheckedIn':
            return !data.participant.isOnsite
          default:
            return true
        }
      })
    })
  }, [
    participants.data,
    questionnaireResults,
    dietQuestionnaireResults,
    appointmentsByParticipantId,
    selectedLocationFilters,
  ])

  const flagOptions = useMemo(
    () =>
      uniqBy(
        (participants.data ?? []).flatMap((p) => p.flags.map((f) => f.coding)),
        (flag) => flag.code
      ).map((flag) => ({
        value: flag.code,
        label: flag.display,
      })),
    [participants.data]
  )

  const columnConfigs = useMemo<ColumnConfig<DailyAttendanceItemData>[]>(
    () => [
      {
        id: 'rowNumber',
        headerText: '#',
        render: (data: DailyAttendanceItemData) => (
          <span className="text-muted-foreground">
            {String(data.rowNumber).padStart(2, '0')}
          </span>
        ),
        valueType: 'number',
        getSortFilterValue: (data: DailyAttendanceItemData) => data.rowNumber,
        enableSorting: true,
        size: 40,
      },
      {
        id: 'participant',
        headerText: 'Participant Name',
        render: (data: DailyAttendanceItemData) => (
          <PatientCard participant={data.participant} />
        ),
        valueType: 'text',
        getSortFilterValue: (data: DailyAttendanceItemData) =>
          data.participant.searchableText,
        enableSorting: true,
      },
      {
        id: 'flags',
        headerText: 'Flags',
        render: (data: DailyAttendanceItemData) => (
          <div className="flex flex-wrap gap-1">
            <ParticipantFlags participant={data.participant} />
          </div>
        ),
        valueType: 'enum' as const,
        getSortFilterValue: (data: DailyAttendanceItemData) =>
          data.participant.flags.map((f) => f.coding.code).join(' '),
        filteringType: flagOptions.length > 0 ? 'multi-select' : undefined,
        options: flagOptions,
      },
      {
        id: 'appointments',
        headerText: 'Appointments',
        render: (data: DailyAttendanceItemData) => (
          <div className="flex flex-col gap-2 whitespace-normal">
            {data.appointments.map((appointment) => (
              <AppointmentDisplay
                key={appointment.id}
                appointment={appointment}
                onClick={() => {
                  setSelectedAppointment(appointment)
                }}
              />
            ))}
          </div>
        ),
        valueType: 'number',
        getSortFilterValue: (data: DailyAttendanceItemData) =>
          data.appointments.length,
      },
      {
        id: 'diet',
        headerText: 'Dietary Needs',
        render: (data: DailyAttendanceItemData) => {
          const dietResponse = data.dietQuestionnaireResponse
          if (!dietResponse) {
            return null
          }

          const diet = dietResponse.getAnswerValue('diet')
          const fluidConsistency =
            dietResponse.getAnswerValue('fluid-consistency')
          const restrictions = dietResponse.getAnswerValue('fluid-restrictions')
          const supplements = dietResponse.getAnswerValue('supplements')

          return (
            <div className="flex flex-col gap-1 text-sm">
              {diet && (
                <div>
                  <span className="font-medium">Diet:</span> {String(diet)}
                </div>
              )}
              {fluidConsistency && (
                <div>
                  <span className="font-medium">Fluid:</span>{' '}
                  {String(fluidConsistency)}
                </div>
              )}
              {restrictions && (
                <div>
                  <span className="font-medium">Restrictions:</span>{' '}
                  {String(restrictions)}
                </div>
              )}
              {supplements && (
                <div>
                  <span className="font-medium">Supplements:</span>{' '}
                  {String(supplements)}
                </div>
              )}
            </div>
          )
        },
        valueType: 'text',
        getSortFilterValue: (data: DailyAttendanceItemData) => {
          const diet = data.dietQuestionnaireResponse?.getAnswerValue('diet')
          return diet ? String(diet) : ''
        },
        enableSorting: true,
      },
      {
        id: 'notes',
        headerText: 'Notes',
        render: (data: DailyAttendanceItemData) => {
          const headerNoteItem =
            questionnaires.AdditionalInformationQuestionnaire.item.find(
              (item) => item.linkId === 'header-note'
            )
          invariant(headerNoteItem, 'Header note item is required')

          return (
            <ParticipantReferenceContext.Provider
              value={data.participant.reference}
            >
              <QuestionnaireItemForm
                item={headerNoteItem}
                questionnaireResponse={data.questionnaireResponse}
                className="text-foreground-secondary"
                formClassName="w-full"
              />
            </ParticipantReferenceContext.Provider>
          )
        },
      },
      {
        id: 'actions',
        headerText: 'Actions',
        render: (data: DailyAttendanceItemData) => (
          <div className="flex gap-2 text-sm text-muted-foreground">
            {!data.participant.isOnsite && (
              <CheckInButton
                participant={data.participant}
                appointment={data.appointments[0]}
              />
            )}
            {data.participant.isOnsite && (
              <CheckOutButton participant={data.participant} />
            )}
            <AddToMealQueue participant={data.participant} />
          </div>
        ),
      },
    ],
    [flagOptions]
  )

  const navigate = useNavigateFromClickEvent()

  if (appointmentsResult.isLoading || participants.isLoading) {
    return <Loader />
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MiniCalendar
            value={selectedDateAsDate}
            onValueChange={handleDateChange}
            days={5}
            weekdaysOnly
          >
            <MiniCalendarNavigation direction="prev" />
            <MiniCalendarDays>
              {(date) => (
                <MiniCalendarDay key={date.toISOString()} date={date} />
              )}
            </MiniCalendarDays>
            <MiniCalendarNavigation direction="next" />
          </MiniCalendar>
          <FilterChip
            label="Filter"
            options={locationOptions}
            values={selectedLocationFilters}
            onChange={setSelectedLocationFilters}
            disableSearch
          />
          <div className="text-2xs whitespace-nowrap text-foreground-secondary">
            {tableData.length} {pluralize('participant', tableData.length)}
          </div>
        </div>
        <Button
          className="gap-2 print:hidden"
          onClick={() => window.print()}
          variant="outline"
        >
          <IconPrinter className="size-4" />
          Print
        </Button>
      </div>
      <ReactTable<DailyAttendanceItemData>
        columnConfigs={columnConfigs}
        data={tableData}
        tableOptions={{
          enablePagination: false,
          getRowId: (row) => row.participant.id,
          mantineTableBodyCellProps: ({ cell }) => ({
            onClick: (event) => {
              if (
                !['appointments', 'notes', 'actions'].includes(cell.column.id)
              ) {
                const urlPath = `/Patient/${cell.row.original.participant.id}`
                navigate(urlPath, event)
              }
            },
            className: 'cursor-pointer',
          }),
        }}
      />
      <Sheet
        open={!!selectedAppointment}
        onOpenChange={(open) => !open && setSelectedAppointment(undefined)}
      >
        <SheetContent side="right" className="max-w-xl">
          {selectedAppointment && (
            <AppointmentDetails
              appointmentRef={selectedAppointment.reference}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  )
}
